/**
 * apiClient.js — Authenticated fetch with automatic token refresh.
 *
 * Flow:
 *  1. Make request with current accessToken
 *  2. If 401 → try to refresh using refreshToken
 *  3. If refresh succeeds → save new accessToken → retry original request
 *  4. If refresh fails → clear storage → redirect to login
 */
import { BASE_URL } from "../config";

export function getAccessToken() {
  return localStorage.getItem("accessToken");
}

export function getRefreshToken() {
  return localStorage.getItem("refreshToken");
}

function clearSession() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  // Hard redirect to login — works without needing React context
  window.location.href = "/";
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearSession();
    return null;
  }

  try {
    const res = await fetch(`${BASE_URL}api/accounts/token/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!res.ok) {
      // Refresh token is expired or invalid → force logout
      clearSession();
      return null;
    }

    const data = await res.json();
    const newAccess = data.access;
    localStorage.setItem("accessToken", newAccess);
    return newAccess;
  } catch {
    clearSession();
    return null;
  }
}

/**
 * Drop-in replacement for fetch() that auto-attaches the Bearer token
 * and handles 401 → refresh → retry transparently.
 */
export async function authFetch(url, options = {}) {
  const token = getAccessToken();

  const makeRequest = (accessToken) =>
    fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
        Authorization: `Bearer ${accessToken}`,
      },
    });

  let res = await makeRequest(token);

  // Token expired → try to refresh once
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (!newToken) return res; // session cleared, page will redirect
    res = await makeRequest(newToken);
  }

  return res;
}

/**
 * Convenience wrapper — like authFetch but parses JSON and throws on error.
 */
export async function authFetchJSON(url, options = {}) {
  const res = await authFetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || data?.detail || `Request failed (${res.status})`);
  }
  return data;
}
